import { useEffect, useRef, useState } from "react";
import { settingsSections } from "./conference-settings.constants";
import s from "./conference-settings-modal.module.scss";

export const ConferenceSettingsSidebar = ({ activeSection, onSectionChange }) => {
  const navRef = useRef(null);
  const indicatorRef = useRef(null);
  const dragStateRef = useRef({
    pointerId: null,
    startX: 0,
    startOffset: 0,
  });
  const [scrollState, setScrollState] = useState({
    showIndicator: false,
    thumbWidth: 0,
    thumbOffset: 0,
  });

  useEffect(() => {
    const navNode = navRef.current;
    if (!navNode || typeof window === "undefined") {
      return undefined;
    }

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = navNode;

      if (scrollWidth <= clientWidth) {
        setScrollState({
          showIndicator: false,
          thumbWidth: 0,
          thumbOffset: 0,
        });
        return;
      }

      const trackWidth = clientWidth;
      const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 36);
      const maxOffset = Math.max(trackWidth - thumbWidth, 0);
      const maxScrollLeft = Math.max(scrollWidth - clientWidth, 1);
      const thumbOffset = (scrollLeft / maxScrollLeft) * maxOffset;

      setScrollState({
        showIndicator: true,
        thumbWidth,
        thumbOffset,
      });
    };

    updateScrollState();

    navNode.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    let resizeObserver;
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(navNode);
    }

    return () => {
      navNode.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, []);

  const scrollNavToOffset = (nextOffset) => {
    const navNode = navRef.current;
    const indicatorNode = indicatorRef.current;
    if (!navNode || !indicatorNode) {
      return;
    }

    const trackWidth = indicatorNode.clientWidth;
    const maxOffset = Math.max(trackWidth - scrollState.thumbWidth, 0);
    const clampedOffset = Math.min(Math.max(nextOffset, 0), maxOffset);
    const maxScrollLeft = Math.max(navNode.scrollWidth - navNode.clientWidth, 0);
    const nextScrollLeft = maxOffset > 0 ? (clampedOffset / maxOffset) * maxScrollLeft : 0;

    navNode.scrollTo({
      left: nextScrollLeft,
      behavior: "auto",
    });
  };

  const handleIndicatorPointerDown = (event) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    const indicatorNode = indicatorRef.current;
    if (!indicatorNode) {
      return;
    }

    const rect = indicatorNode.getBoundingClientRect();
    scrollNavToOffset(event.clientX - rect.left - scrollState.thumbWidth / 2);
  };

  const handleThumbPointerDown = (event) => {
    const indicatorNode = indicatorRef.current;
    if (!indicatorNode) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startOffset: scrollState.thumbOffset,
    };

    indicatorNode.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handleIndicatorPointerMove = (event) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;
    scrollNavToOffset(dragStateRef.current.startOffset + deltaX);
  };

  const handleIndicatorPointerEnd = (event) => {
    const indicatorNode = indicatorRef.current;
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    indicatorNode?.releasePointerCapture?.(event.pointerId);
    dragStateRef.current = {
      pointerId: null,
      startX: 0,
      startOffset: 0,
    };
  };

  return (
    <aside className={s.sidebar}>
      <div className={s.sidebarTitle}>Настройки</div>
      <div ref={navRef} className={s.sidebarNav}>
        {settingsSections.map(({ key, label, mobileLabel, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`${s.sidebarItem} ${activeSection === key ? s.sidebarItemActive : ""}`.trim()}
            onClick={() => onSectionChange(key)}
          >
            <Icon size={20} />
            <span className={s.sidebarItemLabel}>{label}</span>
            <span className={s.sidebarItemMobileLabel}>{mobileLabel ?? label}</span>
          </button>
        ))}
      </div>
      <div
        ref={indicatorRef}
        className={`${s.sidebarNavIndicator} ${scrollState.showIndicator ? s.sidebarNavIndicatorVisible : ""}`.trim()}
        aria-hidden="true"
        onPointerDown={handleIndicatorPointerDown}
        onPointerMove={handleIndicatorPointerMove}
        onPointerUp={handleIndicatorPointerEnd}
        onPointerCancel={handleIndicatorPointerEnd}
      >
        <div
          className={s.sidebarNavIndicatorThumb}
          style={{
            width: `${scrollState.thumbWidth}px`,
            transform: `translateX(${scrollState.thumbOffset}px)`,
          }}
          onPointerDown={handleThumbPointerDown}
        />
      </div>
    </aside>
  );
};
